import { useState, useCallback, useMemo } from 'react';
import { updateVehicle } from '../api/vehicleService';
import { saveDispatchReservation } from '../api/dispatchService';
import { getVehicleZonesConfig } from '../config/vehicleZones';
import { getMoveChoicesForStatusCode } from '../lib/vehicleZoneMove';
import type { ZoneMovePlan } from '../lib/vehicleZoneMove';
import type { Vehicle, StatusItem, Reservation } from '../types';

const CHECK_FIELDS = ['cleaning', 'car_wash', 'check1', 'check2', 'tire_groove', 'tire_pressure'];

// ゾーン定義（config/vehicleZones.ts）から車両ステータス code の集合を作る。
// リストのステータス変更を、車両ポジションの DnD と同じ規則で動かすため、
// 店舗ごとの構成差（那覇=赤嶺駅 / 宮古=下地島）はここで二重管理しない。
const statusCodesOfZones = (zoneIds: Set<string>): Set<string> => {
  const cfg = getVehicleZonesConfig();
  return new Set(
    cfg.zones
      .filter(z => zoneIds.has(z.id) && z.statusCode)
      .map(z => z.statusCode as string),
  );
};

/** ドロップオフ系（那覇: 送り中 / 赤嶺駅D.O、宮古: 送り中 / 下地島D.O） */
const DROPOFF_STATUS_CODES = statusCodesOfZones(getVehicleZonesConfig().dropoffZoneIds);
/** ピックアップ系（那覇: 迎え中 / 赤嶺駅P.U、宮古: 迎え中 / 下地島P.U） */
const PICKUP_STATUS_CODES = statusCodesOfZones(getVehicleZonesConfig().pickupZoneIds);
/**
 * 場所を表すエリアのステータスか（そのエリアに置くこと自体が「そこにある」意味で、移動ステータスを付けない）。
 * = 車両ポジションで置けるゾーンの車両ステータスのうち、移動ステータスの対応表（zoneLocationStatus）に無いもの
 */
const isPlaceZoneStatusCode = (code: string): boolean => {
  if (!code) return false;
  const cfg = getVehicleZonesConfig();
  const zones = cfg.zones.filter(z => z.droppable !== false && z.statusCode === code);
  return zones.length > 0 && zones.every(z => !cfg.zoneLocationStatus[z.id]);
};

/** 店舗に戻す＝返却完了とみなすステータス */
const SHOP_STATUS_CODE = 'airport_store';

interface StatusModalState {
  open: boolean;
  vehicle: Vehicle | null;
  reservation: Reservation | null;
}

interface LocationStatusModalState {
  open: boolean;
  vehicle: Vehicle | null;
  listType: string | null;
  reservation: Reservation | null;
}

/** 行き先（ゾーン）選択モーダル。迎え中/送り中は行き先が複数あるので聞く（2026-08-25） */
interface MoveTargetModalState {
  open: boolean;
  vehicle: Vehicle | null;
  reservation: Reservation | null;
  choices: ZoneMovePlan[];
}

interface ConfirmDialogState {
  open: boolean;
  vehicle: Vehicle | null;
  statusId: number | null;
  reservation: Reservation | null;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: string;
}

interface User {
  id: string | number;
  [key: string]: any;
}

/**
 * 画面側の state 更新先（2026-09-21）。
 * PC の daylist はローカル state（setVehicles / setDepartures / setReturns）、
 * モバイル PWA は同期キャッシュ（vehicleSyncService / reservationSyncService の applyLocalUpdate）
 * が正なので、ここを差し替えられるようにして**判断ロジック（確認・行き先・運転者・予約連動）を
 * 1つにする**。PC は従来どおり setter を渡せば同じ動きになる。
 */
export interface StatusDialogAdapters {
  /** 楽観更新 / 確定後の車両を反映する */
  applyVehicle: (vehicle: Vehicle) => void;
  /** 予約の状態変更（利用中 / 貸出済み）を反映する */
  applyReservation: (reservation: Reservation) => void;
}

interface UseStatusDialogsOptions {
  user: User | null;
  vehicleStatusList: StatusItem[];
  locationStatusList: StatusItem[];
  setVehicles?: React.Dispatch<React.SetStateAction<Record<string, Vehicle>>>;
  setDepartures?: React.Dispatch<React.SetStateAction<Reservation[]>>;
  setReturns?: React.Dispatch<React.SetStateAction<Reservation[]>>;
  /** setter の代わりに反映先を差し替える（モバイル PWA） */
  adapters?: StatusDialogAdapters;
  /** トーストを画面側の仕組みで出す場合（モバイル PWA）。未指定ならフック内の toast state に入る */
  onToast?: (message: string, severity: string) => void;
  /**
   * ピックアップ/ドロップオフ系のステータスに変えた直後に運転者を聞くためのコールバック。
   * 車両ポジションで同じゾーンへ DnD したときと同じ体験にする（2026-08-25）。
   */
  onRequestDriver?: (reservation: Reservation, listType: 'departure' | 'return') => void;
}

export function useStatusDialogs({
  user,
  vehicleStatusList,
  locationStatusList,
  setVehicles,
  setDepartures,
  setReturns,
  adapters,
  onToast,
  onRequestDriver,
}: UseStatusDialogsOptions) {
  const [statusModal, setStatusModal] = useState<StatusModalState>({ open: false, vehicle: null, reservation: null });
  const [moveTargetModal, setMoveTargetModal] = useState<MoveTargetModalState>({ open: false, vehicle: null, reservation: null, choices: [] });
  const [locationStatusModal, setLocationStatusModal] = useState<LocationStatusModalState>({ open: false, vehicle: null, listType: null, reservation: null });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({ open: false, vehicle: null, statusId: null, reservation: null });
  const [toastState, setToastState] = useState<ToastState>({ open: false, message: '', severity: 'success' });
  const toast = toastState;
  // onToast が渡されていれば画面側の仕組みへ流す（PWA）。無ければ従来どおりフック内の state（PC）
  const setToast = useCallback((next: ToastState) => {
    if (onToast && next.open) onToast(next.message, next.severity);
    else setToastState(next);
  }, [onToast]);

  const handleCloseToast = useCallback(() => {
    setToastState(prev => ({ ...prev, open: false }));
  }, []);

  // state の反映先。adapters 優先、無ければ setter（PC）
  const applyVehicle = useCallback((v: Vehicle) => {
    if (adapters) adapters.applyVehicle(v);
    else setVehicles?.(prev => ({ ...prev, [v.id]: v }));
  }, [adapters, setVehicles]);
  const applyReservation = useCallback((r: Reservation) => {
    if (adapters) { adapters.applyReservation(r); return; }
    const updateRes = (x: Reservation) => (String(x.id) === String(r.id) ? r : x);
    setDepartures?.(prev => prev.map(updateRes));
    setReturns?.(prev => prev.map(updateRes));
  }, [adapters, setDepartures, setReturns]);

  // 移動ステータスの id も店舗ごとに採番が違う。マスタに無い code は null
  //（宮古は vehicle_location マスタ未整備なので現状すべて null になる）
  const locationIdByCode = useCallback((code: string | null): number | null => {
    if (!code) return null;
    const hit = (locationStatusList || []).find((s: any) => s?.code === code);
    return typeof hit?.id === 'number' ? hit.id : null;
  }, [locationStatusList]);

  // 車両ステータスの id は店舗ごとに採番が違うので、必ず code から解決する
  const statusIdByCode = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    (vehicleStatusList || []).forEach((s: any) => {
      if (s?.code && typeof s.id === 'number') map[s.code] = s.id;
    });
    return map;
  }, [vehicleStatusList]);

  const executeStatusUpdate = useCallback(async (
    vehicle: Vehicle,
    statusId: number,
    clearChecks: boolean,
    reservation: Reservation | null = null,
    /** 行き先が決まっている場合（迎え中/送り中）。移動ステータスと運転者の相手が決まる */
    plan: ZoneMovePlan | null = null,
  ) => {
    const statusObj = vehicleStatusList.find(s => s.id === statusId);
    const newStatusName = statusObj ? statusObj.label : '';
    const departingId = statusIdByCode['departing'];
    const targetCode = (statusObj as any)?.code || '';

    // 同じ状態への操作は何もせず終了（車両ポジションの DnD と統一。2026-08-25）。
    // 行き先つきの場合は移動ステータスも一致していなければ「同じ」ではない
    const sameStatus = Number(vehicle.status) === Number(statusId);
    const planLocationId = plan ? locationIdByCode(plan.locationStatusCode) : null;
    const sameLocation = !plan || (vehicle as any).location_status === planLocationId;
    if (sameStatus && sameLocation) return;

    const updates: Record<string, any> = { status: statusId };
    // 行き先が決まっているなら移動ステータスも一緒に入れる（車両ポジションの DnD と同じ）。
    // リスト側は従来「移動ステータス変更」が別モーダルで、2回操作しないと同じ状態に
    // ならなかった
    if (plan) updates.location_status = planLocationId;
    // 場所を表すエリアのステータス（店舗・ヤード・三井P 等。移動ステータスの対応表に無いゾーン）にしたら
    // 移動ステータスはクリアする（車両ポジションの DnD と同じ表。2026-10-24 前田さん確認）。
    // ゾーンに対応しないステータス（修理・米軍など）は従来どおり移動ステータスに触らない
    else if (isPlaceZoneStatusCode(targetCode)) updates.location_status = null;
    // 出発中にしたら移動ステータスは消す（車両ポジションの 貸出(H)/利用中(E) への DnD と同じ）。
    // ここだけ消しておらず「出発中なのに →店舗 が残る」車両が出ていた（2026-09-19 前田さん指摘）
    if (departingId != null && statusId === departingId) updates.location_status = null;
    if (clearChecks) {
      if (vehicle.check_situations && Array.isArray(vehicle.check_situations)) {
        updates.check_situations = vehicle.check_situations.map((item: any) => ({ ...item, value: false }));
      } else {
        CHECK_FIELDS.forEach(field => {
          updates[field] = false;
        });
      }
    }

    const updatedVehicle = { ...vehicle, ...updates, status_name: newStatusName };
    if (updates.check_situations) {
      updatedVehicle.check_situations = updates.check_situations;
    }

    applyVehicle(updatedVehicle);

    try {
      const savedVehicle = await updateVehicle({ id: vehicle.id, ...updates }, user?.id);
      // サーバの確定値（status_name 等を含む）で上書き。PWA の同期キャッシュはこれが要る
      if (savedVehicle) applyVehicle({ ...updatedVehicle, ...(savedVehicle as any) });

      // 出発中に変更 → 対応する予約を「利用中」(3) に自動更新
      if (departingId != null && statusId === departingId && reservation) {
        if (String(reservation.status) !== '3' && reservation.status !== 'in_use') {
          try {
            const savedRes = await saveDispatchReservation({ ...reservation, status: 3, status_code: 'in_use' } as any);
            applyReservation({ ...reservation, status: 3, status_code: 'in_use', ...((savedRes as any) || {}) } as any);
            setToast({ open: true, message: '車両を出発中に変更し、予約内容を利用中に更新しました', severity: 'success' });
          } catch (resError) {
            console.error('Failed to auto-update reservation status:', resError);
            setToast({ open: true, message: '車両ステータスを更新しました（予約の自動更新に失敗しました）', severity: 'warning' });
          }
          return;
        }
      }

      setToast({ open: true, message: 'ステータスを更新しました', severity: 'success' });

      // ピックアップ/ドロップオフ系へ移したら運転者を聞く（車両ポジションの DnD と同じ）。
      // 迎え中→貸出の運転者 / 送り中→返却の運転者。
      // 行き先を聞いた場合は、その回答が終わってからこのモーダルが出る。
      const driverType = plan?.driverType
        ?? (PICKUP_STATUS_CODES.has(targetCode) ? 'departure'
          : DROPOFF_STATUS_CODES.has(targetCode) ? 'return' : null);
      if (onRequestDriver && reservation && driverType) {
        setTimeout(() => onRequestDriver(reservation, driverType), 300);
      }

      // 出発中 → **店舗** に戻した時だけ、対応する予約を「貸出済」(2) に自動更新する
      // （2026-08-25。車両ポジションに合わせて範囲を絞った）。
      // 車両ポジションで予約が貸出済みになるのは
      //   ・利用中 → 店舗
      //   ・ドロップオフ → ドロップオフ以外
      // の2つだけで、迎え中・ヤード等へ移しただけでは変わらない。
      // 以前のリストは「出発中から何かに変えたら貸出済み」だったため、
      // 迎え中にしただけで返却予約が完了扱いになっていた。
      const leftDeparting = departingId != null
        && Number(vehicle.status) === departingId
        && statusId !== departingId;
      // ドロップオフ（送り中 / 赤嶺駅D.O / 下地島D.O）→ ドロップオフ以外 も貸出済みにする（2026-10-19）。
      // 車両ポジションの DnD はこのルールで貸出済みにしていたが、リスト（PC の行・PWA）のステータス変更には
      // 無く、「送り中 → 店舗」をリストで行うと返却予約が利用中のまま残っていた（10/10 13724 など）。
      // 行の予約が貸出予約（次のお客様）のこともあるので、**利用中の予約だけ**を対象にする
      const currentCode = (vehicleStatusList.find(s => Number(s.id) === Number(vehicle.status)) as any)?.code || '';
      const isInUse = !!reservation && (String(reservation.status) === '3' || reservation.status === 'in_use');
      const leftDropoff = DROPOFF_STATUS_CODES.has(currentCode) && !DROPOFF_STATUS_CODES.has(targetCode) && isInUse;
      if (((leftDeparting && targetCode === SHOP_STATUS_CODE) || leftDropoff) && reservation) {
        if (String(reservation.status) !== '2' && reservation.status !== 'done') {
          try {
            const savedRes = await saveDispatchReservation({ ...reservation, status: 2, status_code: 'done' } as any);
            applyReservation({ ...reservation, status: 2, status_code: 'done', ...((savedRes as any) || {}) } as any);
            setToast({ open: true, message: '車両を返却済みに変更し、予約内容を貸出済(完了)に更新しました', severity: 'success' });
          } catch (resError) {
            console.error('Failed to auto-update reservation status:', resError);
            setToast({ open: true, message: '車両ステータスを更新しました（予約の自動更新に失敗しました）', severity: 'warning' });
          }
        }
      }
    } catch (error) {
      console.error('Failed to update vehicle status:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      applyVehicle(vehicle);
    }
  }, [user, vehicleStatusList, statusIdByCode, locationIdByCode, applyVehicle, applyReservation, setToast, onRequestDriver]);

  const handleStatusClick = useCallback((vehicle: Vehicle, reservation: Reservation | null) => {
    setStatusModal({ open: true, vehicle, reservation });
  }, []);

  /**
   * 車両ステータス変更の入口（判断ロジック）。PC のステータスモーダルも PWA のシートもここを通す:
   *   同じステータス → 何もしない / 出発中 → チェック初期化の確認 /
   *   迎え中・送り中 → 行き先が複数なら聞く / それ以外 → 即適用
   */
  const requestStatusChange = useCallback((vehicle: Vehicle, reservation: Reservation | null, statusId: number) => {
    // 同じステータスへの操作は何もせず終了（車両ポジションの DnD と統一）
    if (Number(vehicle.status) === Number(statusId)) return;

    const departingId = statusIdByCode['departing'];
    if (departingId != null && statusId === departingId && Number(vehicle.status) !== departingId) {
      setConfirmDialog({ open: true, vehicle, statusId, reservation });
      return;
    }

    // 迎え中/送り中 は行き先が複数ある（那覇: 空港 or 赤嶺駅南口）。
    // どこへ行くかで車両ステータス自体も変わる（赤嶺駅なら 赤嶺駅P.U）ため、
    // 先に行き先を聞いてから適用する。運転者モーダルはそのあとに出る。
    const code = (vehicleStatusList.find(s => s.id === statusId) as any)?.code || '';
    const choices = code ? getMoveChoicesForStatusCode(code) : [];
    if (choices.length > 1) {
      setMoveTargetModal({ open: true, vehicle, reservation, choices });
      return;
    }
    if (choices.length === 1) {
      const plan = choices[0];
      executeStatusUpdate(vehicle, statusIdByCode[plan.statusCode] ?? statusId, false, reservation, plan);
      return;
    }

    executeStatusUpdate(vehicle, statusId, false, reservation);
  }, [executeStatusUpdate, statusIdByCode, vehicleStatusList]);

  const handleStatusUpdate = useCallback((statusId: number) => {
    const { vehicle, reservation } = statusModal;
    if (!vehicle) return;
    setStatusModal({ open: false, vehicle: null, reservation: null });
    requestStatusChange(vehicle, reservation, statusId);
  }, [statusModal, requestStatusChange]);

  /** 行き先モーダルで選ばれたゾーンを適用する */
  const handleMoveTargetSelect = useCallback((plan: ZoneMovePlan) => {
    const { vehicle, reservation } = moveTargetModal;
    setMoveTargetModal({ open: false, vehicle: null, reservation: null, choices: [] });
    if (!vehicle) return;
    const planStatusId = statusIdByCode[plan.statusCode];
    if (planStatusId == null) {
      setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
      return;
    }
    executeStatusUpdate(vehicle, planStatusId, false, reservation, plan);
  }, [moveTargetModal, executeStatusUpdate, statusIdByCode]);

  const handleConfirmClose = useCallback((clearChecks: boolean) => {
    const { vehicle, statusId, reservation } = confirmDialog;
    setConfirmDialog({ open: false, vehicle: null, statusId: null, reservation: null });
    if (vehicle && statusId) {
      executeStatusUpdate(vehicle, statusId, clearChecks, reservation);
    }
  }, [confirmDialog, executeStatusUpdate]);

  const handleLocationStatusClick = useCallback((vehicle: Vehicle, listType: string, reservation: Reservation | null) => {
    setLocationStatusModal({ open: true, vehicle, listType, reservation });
  }, []);

  const handleLocationStatusUpdate = useCallback(async (locationStatusId: number) => {
    const vehicle = locationStatusModal.vehicle;
    if (!vehicle) return;

    setLocationStatusModal({ open: false, vehicle: null, listType: null, reservation: null });

    const locationStatus = locationStatusList.find(s => s.id === locationStatusId);
    const newLocationStatusName = locationStatus?.label || null;

    const updatedVehicle = {
      ...vehicle,
      location_status: locationStatusId,
      location_status_name: newLocationStatusName,
      location_status_code: locationStatus?.code || null,
    };
    applyVehicle(updatedVehicle);

    try {
      const saved = await updateVehicle({ id: vehicle.id, location_status: locationStatusId } as any, user?.id);
      if (saved) applyVehicle({ ...updatedVehicle, ...(saved as any) });
      setToast({ open: true, message: '移動ステータスを更新しました', severity: 'success' });
    } catch (error) {
      console.error('Failed to update location status:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      applyVehicle(vehicle);
    }
  }, [locationStatusModal, locationStatusList, user, applyVehicle, setToast]);

  return {
    moveTargetModal,
    setMoveTargetModal,
    handleMoveTargetSelect,
    statusModal,
    setStatusModal,
    locationStatusModal,
    setLocationStatusModal,
    confirmDialog,
    toast,
    setToast,
    handleCloseToast,
    executeStatusUpdate,
    requestStatusChange,
    handleStatusClick,
    handleStatusUpdate,
    handleConfirmClose,
    handleLocationStatusClick,
    handleLocationStatusUpdate,
  };
}
